"use client";

import { Check, Copy, Download, Printer } from "lucide-react";
import { useMemo, useState } from "react";
import { Button, Card, Field, Input, Select, Textarea } from "@/components/ui";
import { download } from "@/lib/utils";

type Ton = "resmi" | "samimi";

export function CoverLetterTool() {
  const [ad, setAd] = useState("");
  const [pozisyon, setPozisyon] = useState("");
  const [sirket, setSirket] = useState("");
  const [yetkili, setYetkili] = useState("");
  const [guclu, setGuclu] = useState("");
  const [neden, setNeden] = useState("");
  const [ton, setTon] = useState<Ton>("resmi");
  const [kopyalandi, setKopyalandi] = useState(false);

  const mektup = useMemo(
    () => olustur({ ad, pozisyon, sirket, yetkili, guclu, neden, ton }),
    [ad, pozisyon, sirket, yetkili, guclu, neden, ton],
  );

  async function kopyala() {
    await navigator.clipboard.writeText(mektup);
    setKopyalandi(true);
    setTimeout(() => setKopyalandi(false), 1800);
  }

  function yazdir() {
    const w = window.open("", "_blank");
    if (!w) return;
    w.document.write(
      `<pre style="font-family:Georgia,serif;font-size:15px;line-height:1.7;white-space:pre-wrap;padding:40px;max-width:720px;margin:auto">${mektup.replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" })[c]!)}</pre>`,
    );
    w.document.close();
    w.focus();
    w.print();
  }

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <div className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Ad Soyad">
            <Input value={ad} onChange={(e) => setAd(e.target.value)} placeholder="Ayşe Demir" />
          </Field>
          <Field label="Başvurulan pozisyon">
            <Input
              value={pozisyon}
              onChange={(e) => setPozisyon(e.target.value)}
              placeholder="Pazarlama Uzmanı"
            />
          </Field>
          <Field label="Şirket adı">
            <Input
              value={sirket}
              onChange={(e) => setSirket(e.target.value)}
              placeholder="Örnek A.Ş."
            />
          </Field>
          <Field label="Yetkili / kişi (opsiyonel)" hint="Boş bırakırsanız “İlgili Makama” yazılır.">
            <Input
              value={yetkili}
              onChange={(e) => setYetkili(e.target.value)}
              placeholder="İnsan Kaynakları"
            />
          </Field>
        </div>

        <Field label="Güçlü yönleriniz / deneyiminiz" hint="Kısa cümleler ya da virgülle ayrılmış birkaç madde yazın.">
          <Textarea
            rows={3}
            value={guclu}
            onChange={(e) => setGuclu(e.target.value)}
            placeholder="5 yıl dijital pazarlama deneyimi, kampanya yönetimi, veri analizi"
          />
        </Field>

        <Field label="Neden bu şirket / pozisyon? (opsiyonel)">
          <Textarea
            rows={2}
            value={neden}
            onChange={(e) => setNeden(e.target.value)}
            placeholder="Markanızın yenilikçi yaklaşımına katkı sunmak istiyorum."
          />
        </Field>

        <Field label="Ton">
          <Select value={ton} onChange={(e) => setTon(e.target.value as Ton)}>
            <option value="resmi">Resmi</option>
            <option value="samimi">Samimi</option>
          </Select>
        </Field>
      </div>

      {/* Canlı önizleme */}
      <div className="space-y-3">
        <Card className="min-h-[320px] whitespace-pre-wrap font-serif text-[15px] leading-relaxed">
          {mektup}
        </Card>
        <div className="flex flex-wrap gap-3">
          <Button onClick={kopyala}>
            {kopyalandi ? <Check className="size-4" /> : <Copy className="size-4" />}
            {kopyalandi ? "Kopyalandı" : "Kopyala"}
          </Button>
          <Button variant="outline" onClick={() => download(mektup, "on-yazi.txt", "text/plain;charset=utf-8")}>
            <Download className="size-4" />
            .txt indir
          </Button>
          <Button variant="ghost" onClick={yazdir}>
            <Printer className="size-4" />
            Yazdır / PDF
          </Button>
        </div>
      </div>
    </div>
  );
}

function olustur(v: {
  ad: string;
  pozisyon: string;
  sirket: string;
  yetkili: string;
  guclu: string;
  neden: string;
  ton: Ton;
}) {
  const poz = v.pozisyon.trim() || "[pozisyon]";
  const sir = v.sirket.trim() || "[şirket]";
  const selam = v.yetkili.trim() ? `Sayın ${v.yetkili.trim()},` : "İlgili Makama,";

  const guclu = v.guclu.trim();
  const gucluCumle = guclu
    ? v.ton === "resmi"
      ? `Sahip olduğum ${birlestir(guclu)} gibi niteliklerin bu pozisyonun gereklilikleriyle örtüştüğüne inanıyorum.`
      : `${birlestir(guclu, true)} konularındaki deneyimimin bu rolde fark yaratacağını düşünüyorum.`
    : "";

  const nedenCumle = v.neden.trim()
    ? `${v.neden.trim().replace(/\s*\.?\s*$/, "")}.`
    : "";

  const acilis =
    v.ton === "resmi"
      ? `${sir} bünyesinde açık olan ${poz} pozisyonu için başvurumu değerlendirmenize sunmak isterim.`
      : `${sir} şirketindeki ${poz} ilanınızı gördüm ve heyecanla başvurmak istedim.`;

  const kapanis =
    v.ton === "resmi"
      ? "Deneyim ve yetkinliklerimi ekibinize katma fırsatını bir görüşmede detaylandırmaktan memnuniyet duyarım. Başvurumu değerlendirdiğiniz için teşekkür ederim."
      : "Sizinle tanışıp neler katabileceğimi konuşmayı çok isterim. Ayırdığınız zaman için şimdiden teşekkürler!";

  const govde = [acilis, gucluCumle, nedenCumle, kapanis].filter(Boolean).join("\n\n");

  return `${selam}\n\n${govde}\n\nSaygılarımla,\n${v.ad.trim() || "[Ad Soyad]"}`;
}

/** "a, b, c" → "a, b ve c"; samimi modda küçük harfle başlatır. */
function birlestir(metin: string, kucuk = false) {
  const parcalar = metin
    .split(/[,;\n]+/)
    .map((s) => s.trim())
    .filter(Boolean);
  let sonuc: string;
  if (parcalar.length <= 1) sonuc = parcalar[0] ?? metin;
  else sonuc = `${parcalar.slice(0, -1).join(", ")} ve ${parcalar[parcalar.length - 1]}`;
  return kucuk ? sonuc.charAt(0).toLocaleLowerCase("tr") + sonuc.slice(1) : sonuc;
}
